<template>
  <button
      class="toggle-btn btn"
      :class="{ 'shifted': isOpen,
        'btn-outline-secondary': isOpen,
        'btn-secondary': !isOpen}"
      @click="togglePanel"
  >
    <span v-if="!isOpen && first"> Create image </span>
    <font-awesome-icon v-if="!isOpen" :icon="['fas', 'sparkles']" size="xl"></font-awesome-icon>
    <font-awesome-icon v-else :icon="['fas', 'xmark']" size="xl"></font-awesome-icon>

  </button>

  <Transition name="slide">
    <div v-if="isOpen" class="side-panel border-end border-secondary">
      <div class="panel-content">
        <div class="my-3">

          <div class="main-form-container image-description-form p-3">
            <div class="rounded form-block mb-4 generator-input">
              <h5>What do you like to create</h5>
              <generation-notice/>

              <div class="form-group">


                <DynamicSelection :model-value="generatorConfig"
                                  group="basic"
                                  @update:model-value="recalculateVisibility"
                                  v-if="generatorConfig"></DynamicSelection>


                <div class="accordion" id="promptBuilderAccordion">
                  <div class="accordion-item">
                    <h2 class="accordion-header" id="accordionSectionDetails">
                      <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                              data-bs-target="#collapseOne"
                              aria-expanded="true" aria-controls="collapseOne">
                        Detailed Scene Settings
                      </button>
                    </h2>
                    <div id="collapseOne" class="accordion-collapse collapse" aria-labelledby="accordionSectionDetails"
                         data-bs-parent="#promptBuilderAccordion">
                      <div class="accordion-body">
                        <DynamicSelection :model-value="generatorConfig"
                                          group=""
                                          @update:model-value="recalculateVisibility"
                                          v-if="generatorConfig"></DynamicSelection>

                      </div>
                    </div>
                  </div>
                </div>

                <div class="accordion my-2 " id="promptBuilderAccordionHint">

                  <div class="accordion-item">
                    <h2 class="accordion-header" id="accordionSectionHint">
                      <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                              data-bs-target="#collapseHint"
                              aria-expanded="true" aria-controls="collapseHint">
                        Additional prompt hint
                      </button>
                    </h2>
                    <div id="collapseHint" class="accordion-collapse collapse" aria-labelledby="accordionSectionHint"
                         data-bs-parent="#promptBuilderAccordionHint">
                      <div class="accordion-body">

                        <div>
                          <label
                              for="helper-textarea"
                              class="block text-sm font-medium text-gray-700 mb-2"
                          >
                          </label>
                          <textarea
                              id="helper-textarea"
                              v-model="hintValue"
                              class="block w-full rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 w-100"
                              rows="3"
                              placeholder=""
                          ></textarea>
                          <p class="mt-2 text-sm text-gray-500">
                            Add your custom details to the scene
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                <button
                    @click="startGeneration"
                    :disabled="isGenerating || countDown"
                    class="btn btn-primary action-button mb-3"
                >

                  <span v-if="isGenerating" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ isGenerating ? 'Generating...' : 'Generate Image' }} &nbsp;
                  <font-awesome-icon :icon="['fas', 'sparkles']"></font-awesome-icon>
                </button>

                <div v-if="countDown" class="w-100">
                  Generation will start in {{countDown}} seconds<br/>
                  <b>Don't like to wait? <a href="/my-account" target="_blank">Create a free account</a></b>
                </div>

                <generation-progress
                    v-if="isGenerating"
                    :progress="progress"
                />

              </div>

            </div>
            <marketing-banner/>
          </div>

        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import {onMounted, ref, watch} from 'vue'
import GenerationProgress from './GenerationProgress.vue'
import GenerationNotice from './GenerationNotice.vue'
import MarketingBanner from './MarketingBanner.vue'
import DynamicSelection from "./DynamicSelection.vue"
import {HTTP_API_ENDPOINT} from "@/services/endpoints";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

const props = defineProps({
  isGenerating: {
    type: Boolean,
    default: false
  },
  progress: {
    default: 0
  },
  waitTime: {
    default: false
  },
  largeRibbon: {
    default: true
  },
  openAtStart: {
    default: true
  }
})

const emit = defineEmits(['update:generatorConfig', 'generate'])

const generatorConfig = ref(null)
const isOpen = ref(props.openAtStart)
const hintValue = ref("")
const first = ref(props.largeRibbon)
const countDown = ref(0)

function countDownStep(){
  countDown.value -= 1;

  if(countDown.value < 0){
    countDown.value = 0
  }
  if(countDown.value === 0){
    emit('generate');
  } else {
    setTimeout(countDownStep, 1000);
  }
}
function waitTimeReset() {
  const lastResetTime = localStorage.getItem("lastResetTime");
  const currentTime = new Date().getTime();
  const ONE_DAY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  // Check if this is the first time running or if 24 hours have passed
  if (!lastResetTime || (currentTime - parseInt(lastResetTime)) >= ONE_DAY) {
    localStorage.setItem("waitTime", "0");
    localStorage.setItem("lastResetTime", currentTime.toString());
    return 0;
  }
  return parseInt(localStorage.getItem("waitTime") ?? "0");
}
function startGeneration(){
  if(props.waitTime){
    let fixedTime = waitTimeReset();
    countDown.value = fixedTime;
    if(!fixedTime){
      emit('generate');
      return;
    }

    setTimeout(countDownStep, 1000);
  } else {
    emit('generate');
  }
}
const togglePanel = () => {
  isOpen.value = !isOpen.value
  if(isOpen.value){
    first.value = false;
  }
}

const sectionById = {};

// Watch for changes
watch(hintValue, (newValue) => {
  lastPrompt['promptHint'] = newValue;
  emit("update:generatorConfig", lastPrompt)
})

const checkSectionVisibility = (section) => {
  if(section['offWhen']){
    let foundMatch = false;
    for(let conditionOnId in section['offWhen']){
      let conditionOnSection = sectionById[conditionOnId];
      let actualValue = conditionOnSection.value;
      let acceptableValues = section['offWhen'][conditionOnId];

      if(!checkSectionVisibility(conditionOnSection)){
        continue;
      }

      if(acceptableValues.find(item => item === actualValue)){
        foundMatch = true;
        break;
      }
    }
    if(foundMatch){
      section.visible = false;
      return false;
    }
  }


  if(section['when']){
    let foundMatch = false;
    for(let conditionOnId in section['when']){
      let conditionOnSection = sectionById[conditionOnId];
      let actualValue = conditionOnSection.value;
      let acceptableValues = section['when'][conditionOnId];

      if(!checkSectionVisibility(conditionOnSection)){
        continue;
      }

      if(acceptableValues.find(item => item === actualValue)){
        foundMatch = true;
        break;
      }
    }

    section.visible = foundMatch;
  } else {
    section.visible = true;
  }
  return section.visible;
}

const checkSelectionsVisibility = (section) =>{
  section.activeEntries = [];
  for(let item of section.entries){
    if(item['offWhen']) {

      let foundMatch = false;

      for (let conditionOnId in item['offWhen']) {
        let conditionOnSection = sectionById[conditionOnId];
        let actualValue = conditionOnSection.value;
        let acceptableValues = item['offWhen'][conditionOnId];

        if (!conditionOnSection.visible) {
          continue;
        }

        if (acceptableValues.find(item => item === actualValue)) {
          foundMatch = true;
          break;
        }
      }
      if(foundMatch){
        item.visible = false;
        if(section.value === item.value){
          section.value = section.defaultSelection;
        }
        continue;
      }
    }

    if(item['when']) {

      let foundMatch = false;

      for (let conditionOnId in item['when']) {
        let conditionOnSection = sectionById[conditionOnId];
        let actualValue = conditionOnSection.value;
        let acceptableValues = item['when'][conditionOnId];

        if (!conditionOnSection.visible) {
          continue;
        }

        if (acceptableValues.find(item => item === actualValue)) {
          foundMatch = true;
          break;
        }
      }

      item.visible = foundMatch;
      if(!item.visible){
        if(section.value === item.value){
          section.value = section.defaultSelection;
        }
      } else{
        section.activeEntries.push(item);
      }
    } else{
      item.visible = true;
      section.activeEntries.push(item);
    }
  }
}

let lastPrompt = {}

const recalculateVisibility = () => {
  for(let section of generatorConfig.value) {
    sectionById[section.id] = section;
  }

  for(let section of generatorConfig.value) {
    checkSectionVisibility(section);
  }

  for(let section of generatorConfig.value) {
    checkSelectionsVisibility(section);
  }

  let prompt = {};

  for(let section of generatorConfig.value) {
    if(section.visible){
      if(section.value) {
        prompt[section.id] = section.value;
      }
    }
  }

  prompt['promptHint'] = hintValue.value;

  lastPrompt = prompt;
  emit("update:generatorConfig", prompt)
}

onMounted(async () => {
  let response = await fetch(HTTP_API_ENDPOINT + '/generate/prompt-builder-conf.json');
  let conf = await response.json();
  for(let section of conf){
    section.value = section['defaultSelection'];
    section.visible = true;
  }

  generatorConfig.value = conf;
  recalculateVisibility();

})
</script>


<style scoped>
.toggle-btn {
  position: fixed;
  left: 0;
  top: 100px;
  z-index: 1045;
  border-radius: 0 4px 4px 0;
  padding: 10px;
  border-left: none;
  //border: none;
  //color: white;
  transition: left 0.3s;
}

.toggle-btn.shifted {
  left: 399px;
  background-color: rgba(33, 37, 41, 0.95);
  backdrop-filter: blur(8px);
  color: white;
}

.side-panel {
  position: fixed;
  left: 0;
  top: 0;
  width: 400px;
  height: 100vh;
  background-color: rgba(33, 37, 41, 0.95);
  backdrop-filter: blur(8px);
  color: white;
  overflow-y: auto;
  z-index: 1040;
}

.panel-content {
  padding: 0;
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>

